import React, { useEffect, useRef, useState } from 'react';
import { X, ExternalLink, Download, Loader2, ArrowLeft, ZoomIn, ZoomOut } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const absUrl = (url) => (url && url.startsWith('/') ? `${API_URL}${url}` : url);

// Rendu des pages PDF en canvas (pdf.js) : fonctionne sur iOS/Android/desktop,
// contrairement à l'iframe qui n'affiche qu'une page sur Safari mobile.
const usePdfPages = (url, containerRef, zoom) => {
  const [state, setState] = useState({ loading: true, error: null, pages: 0 });

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    let pdfDoc = null;
    const container = containerRef.current;
    setState(s => ({ ...s, loading: true }));

    (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
        pdfDoc = await pdfjs.getDocument({ url: absUrl(url) }).promise;
        if (cancelled || !container) return;
        container.innerHTML = '';
        const width = Math.min(container.clientWidth - 16, 1000) * zoom;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        for (let i = 1; i <= pdfDoc.numPages; i += 1) {
          if (cancelled) return;
          const page = await pdfDoc.getPage(i);
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const viewport = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width = `${viewport.width / dpr}px`;
          canvas.style.height = `${viewport.height / dpr}px`;
          canvas.className = 'mb-3 bg-white shadow-md rounded-sm';
          canvas.style.marginLeft = zoom > 1 ? '0' : 'auto';
          canvas.style.marginRight = zoom > 1 ? '0' : 'auto';
          canvas.style.display = 'block';
          canvas.setAttribute('data-testid', `pdf-page-${i}`);
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
        if (!cancelled) setState({ loading: false, error: null, pages: pdfDoc.numPages });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e?.message || 'Erreur', pages: 0 });
      }
    })();

    return () => {
      cancelled = true;
      try { pdfDoc?.destroy(); } catch { /* noop */ }
    };
  }, [url, containerRef, zoom]);

  return state;
};

const ZOOM_STEPS = [1, 1.5, 2, 3];

export const PortalPdfViewer = ({ url, title, onClose }) => {
  const containerRef = useRef(null);
  const closedRef = useRef(false);
  const [zoom, setZoom] = useState(1);
  const { loading, error, pages } = usePdfPages(url, containerRef, zoom);
  const zoomIn = () => setZoom(z => ZOOM_STEPS[Math.min(ZOOM_STEPS.indexOf(z) + 1, ZOOM_STEPS.length - 1)]);
  const zoomOut = () => setZoom(z => ZOOM_STEPS[Math.max(ZOOM_STEPS.indexOf(z) - 1, 0)]);

  const close = () => {
    if (closedRef.current) return;
    closedRef.current = true;
    onClose();
  };

  // Bouton retour physique / geste retour → ferme la visionneuse au lieu de quitter le portail
  useEffect(() => {
    window.history.pushState({ ...(window.history.state || {}), portalPdfViewer: true }, '');
    const onPop = () => close();
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('popstate', onPop);
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    if (window.history.state?.portalPdfViewer) {
      window.history.back(); // déclenche popstate → close()
    } else {
      close();
    }
  };

  const href = absUrl(url);

  return (
    <div className="fixed inset-0 z-[100] bg-stone-900/95 flex flex-col" data-testid="portal-pdf-viewer">
      <div className="flex items-center gap-2 px-3 py-2 bg-stone-950 text-white border-b border-white/10" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
        <button
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-white/10 active:bg-white/20 transition-colors"
          aria-label="Retour au portail"
          data-testid="pdf-viewer-back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{title || 'Document'}</p>
          {pages > 0 && <p className="text-[11px] text-white/60">{pages} page{pages > 1 ? 's' : ''} · {Math.round(zoom * 100)}%</p>}
        </div>
        <button onClick={zoomOut} disabled={zoom === ZOOM_STEPS[0] || loading} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30 transition-colors" aria-label="Zoom arrière" data-testid="pdf-viewer-zoom-out">
          <ZoomOut size={18} />
        </button>
        <button onClick={zoomIn} disabled={zoom === ZOOM_STEPS[ZOOM_STEPS.length - 1] || loading} className="p-2 rounded-full hover:bg-white/10 disabled:opacity-30 transition-colors" aria-label="Zoom avant" data-testid="pdf-viewer-zoom-in">
          <ZoomIn size={18} />
        </button>
        <a href={href} download className="p-2 rounded-full hover:bg-white/10 transition-colors" aria-label="Télécharger" data-testid="pdf-viewer-download">
          <Download size={18} />
        </a>
        <a href={href} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-colors hidden sm:inline-flex" aria-label="Ouvrir dans un nouvel onglet" data-testid="pdf-viewer-external">
          <ExternalLink size={18} />
        </a>
        <button
          onClick={handleClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          aria-label="Fermer"
          data-testid="pdf-viewer-close"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-1 overflow-auto overscroll-contain p-2 sm:p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 text-white/80" data-testid="pdf-viewer-loading">
            <Loader2 size={28} className="animate-spin mb-3" />
            <p className="text-sm">Chargement du document…</p>
          </div>
        )}
        {error && (
          <div className="max-w-md mx-auto mt-10 bg-white rounded-2xl p-5 text-center" data-testid="pdf-viewer-error">
            <p className="text-sm text-stone-700 mb-3">Impossible d'afficher le document ici.</p>
            <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-medium">
              <ExternalLink size={14} /> Ouvrir le PDF
            </a>
          </div>
        )}
        <div ref={containerRef} />
      </div>

      <div className="sm:hidden px-4 py-3 bg-stone-950 border-t border-white/10" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button
          onClick={handleClose}
          className="w-full py-2.5 rounded-full bg-white text-stone-900 text-sm font-semibold active:scale-[0.98] transition-transform"
          data-testid="pdf-viewer-close-mobile"
        >
          Retour au portail
        </button>
      </div>
    </div>
  );
};
